import { useState } from "react";

export interface AdminSeriesPoint {
  label: string;
  value: number;
}

interface AdminLineChartProps {
  seriesA: AdminSeriesPoint[];
  seriesB: AdminSeriesPoint[];
  height?: number;
}

const W = 900;
const H = 280;
const PAD_L = 42;
const PAD_R = 18;
const PAD_T = 18;
const PAD_B = 30;

function niceMax(value: number): number {
  const raw = Math.max(8, value);
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / magnitude;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * magnitude;
}

export default function AdminLineChart({ seriesA, seriesB, height = H }: AdminLineChartProps) {
  const [hover, setHover] = useState<number | null>(null);

  if (!seriesA || seriesA.length === 0) {
    return (
      <div className="flex h-56 w-full items-center justify-center text-xs text-ink-muted">
        No trend data available for this period
      </div>
    );
  }

  const n = seriesA.length;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const max = niceMax(Math.max(...seriesA.map((d) => d.value), ...(seriesB || []).map((d) => d.value), 1));
  const yTicks = 5;

  const xFor = (i: number) => PAD_L + (i / Math.max(1, n - 1)) * innerW;
  const yFor = (v: number) => PAD_T + innerH - (v / max) * innerH;

  const ptsA = seriesA.map((d, i) => ({ x: xFor(i), y: yFor(d.value), label: d.label, value: d.value }));
  const ptsB = seriesB.map((d, i) => ({ x: xFor(i), y: yFor(d.value), label: d.label, value: d.value }));

  const lineA = ptsA.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaA = `${lineA} L${xFor(n - 1).toFixed(1)},${PAD_T + innerH} L${PAD_L},${PAD_T + innerH} Z`;
  const lineB = ptsB.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaB = `${lineB} L${xFor(n - 1).toFixed(1)},${PAD_T + innerH} L${PAD_L},${PAD_T + innerH} Z`;

  const gridlines = Array.from({ length: yTicks }, (_, i) => {
    const ratio = i / (yTicks - 1);
    const y = PAD_T + innerH - ratio * innerH;
    return { y, val: Math.round(ratio * max) };
  });

  const labelStep = Math.max(1, Math.ceil((n - 1) / 5));
  const xLabels = ptsA.filter((_, i) => i % labelStep === 0 || i === n - 1);

  const hoverA = hover != null ? ptsA[hover] : null;
  const hoverB = hover != null ? ptsB[hover] : null;
  const hoverLeft = hover != null ? (hover / Math.max(1, n - 1)) * 100 : 0;
  const hoverTop = hoverA ? (1 - hoverA.value / max) * 100 : 0;

  return (
    <div
      className="relative w-full"
      style={{ height }}
      onMouseMove={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
        setHover(Math.round(ratio * (n - 1)));
      }}
      onMouseLeave={() => setHover(null)}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="h-full w-full"
        role="img"
        aria-label="Mandate trend chart"
      >
        <defs>
          <linearGradient id="adminCreatedFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1E40AF" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#1E40AF" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="adminActiveFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#059669" stopOpacity="0.2" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0" />
          </linearGradient>
        </defs>

        {gridlines.map((g) => (
          <g key={g.y}>
            <line x1={PAD_L} y1={g.y} x2={W - PAD_R} y2={g.y} stroke="#F1F5F9" strokeWidth="1" />
            <text x={PAD_L - 8} y={g.y + 4} textAnchor="end" className="fill-slate-400" fontSize="11">
              {g.val}
            </text>
          </g>
        ))}

        <path d={areaB} fill="url(#adminActiveFill)" />
        <path d={lineB} fill="none" stroke="#059669" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        <path d={areaA} fill="url(#adminCreatedFill)" />
        <path d={lineA} fill="none" stroke="#1E40AF" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />

        {hover != null && (
          <>
            <circle cx={ptsA[hover].x} cy={ptsA[hover].y} r="4.5" fill="#1E40AF" stroke="#FFFFFF" strokeWidth="2" />
            <circle cx={ptsB[hover].x} cy={ptsB[hover].y} r="4.5" fill="#059669" stroke="#FFFFFF" strokeWidth="2" />
          </>
        )}

        {xLabels.map((p) => (
          <text key={p.label + p.x} x={p.x} y={H - 10} textAnchor="middle" className="fill-slate-400" fontSize="11">
            {p.label}
          </text>
        ))}
      </svg>

      {hoverA && hoverB && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-line bg-ink px-3 py-2 shadow-dropdown"
          style={{ left: `${hoverLeft}%`, top: `${hoverTop}%` }}
        >
          <p className="text-[11px] font-medium text-slate-300">{hoverA.label}</p>
          <p className="mt-1 flex items-center gap-1.5 text-[13px] font-semibold text-white">
            <span className="h-2 w-2 rounded-full bg-[#1E40AF]" />
            {hoverA.value} created
          </p>
          <p className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold text-white">
            <span className="h-2 w-2 rounded-full bg-[#059669]" />
            {hoverB.value} active
          </p>
        </div>
      )}
    </div>
  );
}